import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDocuments } from '../context/DocumentContext';

const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [wsInstance, setWsInstance] = useState(null);
  const { user } = useAuth();
  const { setProcessingDocuments } = useDocuments();
  const WS_URL = process.env.REACT_APP_WS_URL || 'wss://predica.ai/ws';
  console.log(WS_URL);
  const connectWebSocket = useCallback(() => {
    if (!user?.id) {
      console.log('No user ID available');
      return null;
    }
    
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.error('No access token available');
      return null;
    }

    const encodedToken = encodeURIComponent(token);
    const encodedUserId = encodeURIComponent(user.id);
    const wsUrl = `${WS_URL}/document-progress/${encodedUserId}?token=${encodedToken}`;
    console.log('Connecting to WebSocket using URL:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connection established');
      ws.send(JSON.stringify({
        type: 'auth',
        token: localStorage.getItem('access_token')
      }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('WebSocket message received:', data);
      
      if (data.type === 'refresh_documents') {
        console.log('Received refresh trigger, fetching documents...');
        setRefreshTrigger(prev => prev + 1);
      } else if (data.document_id) {
        setProcessingDocuments(prev => {
          const newState = {...prev};
          
          if (data.status === 'completed' || data.status === 'failed') {
            console.log(`Removing completed/failed document ${data.document_id} from processing state`);
            delete newState[data.document_id];
            setRefreshTrigger(prev => prev + 1);
          } else {
            newState[data.document_id] = {
              ...(prev[data.document_id] || { 
                filename: data.filename || 'New document',
                uploaded_at: new Date().toISOString()
              }),
              progress: data.progress,
              status: data.status === 'pending' ? 'processing' : data.status,
            };
            console.log(`Updated processing document ${data.document_id}:`, newState[data.document_id]);
          }
          
          return newState;
        });
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed, attempting reconnect...');
      setTimeout(() => connectWebSocket(), 3000);
    };

    setWsInstance(ws);
    return ws;
  }, [user?.id]);

  useEffect(() => {
    let ws;
    console.log('WebSocketContext useEffect triggered');
    // console.log(user?.id);
    // console.log(user);
    if (user?.id) {
      console.log('User ID available, connecting to WebSocket');
      ws = connectWebSocket();
    }

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [connectWebSocket, user?.id]);

  return (
    <WebSocketContext.Provider value={{ 
      connectWebSocket,
      refreshTrigger,
      isConnected: wsInstance?.readyState === WebSocket.OPEN
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext); 